import { useMemo } from "react";
import type { Department } from "../../../api/api";
import type { DepartmentAmounts, OrderPriceByDepartmentResponse } from "../ordersApi";
import { useOrderPriceByDepartment } from "./useOrderPriceByDepartment";

export interface FinalInvoiceDepartmentInfo {
  readonly department: Department;
  readonly label: string;
  readonly totalAmount: number;
  readonly alreadyPaidAmount: number;
}

const DEPARTMENT_LABELS: Record<Department, string> = {
  MARITIME_TRANSPORT: "Transport morski",
  HOTEL: "Hotel",
  ATTRACTION: "Atrakcje",
};

function amountsByDepartment(data: OrderPriceByDepartmentResponse): Partial<Record<Department, DepartmentAmounts>> {
  return data.sellerAssignmentOrder.reduce<Partial<Record<Department, DepartmentAmounts>>>((result, sellerId) => {
    const sellerLimits = data.proforma[sellerId];
    return sellerLimits ? { ...result, ...sellerLimits.byDepartment } : result;
  }, {});
}

export function finalInvoiceDepartments(
  data: OrderPriceByDepartmentResponse | undefined,
): FinalInvoiceDepartmentInfo[] {
  if (!data) {
    return [];
  }
  const amounts = amountsByDepartment(data);
  return data.departmentAssignmentOrder
    .filter((department) => (amounts[department]?.amount ?? 0) > 0)
    .map((department) => {
      const { amount, availableAmount } = amounts[department]!;
      return {
        department,
        label: DEPARTMENT_LABELS[department],
        totalAmount: amount,
        alreadyPaidAmount: Math.max(0, amount - availableAmount),
      };
    });
}

export function useFinalInvoiceDepartmentPayments(orderId: string | undefined): FinalInvoiceDepartmentInfo[] {
  const { data } = useOrderPriceByDepartment(orderId);
  return useMemo(() => finalInvoiceDepartments(data), [data]);
}
